import React, {useEffect, useState} from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import type {RonaldTask} from './RonaldStatusStrip';

interface RonaldFutureMessageCardProps {
    task: RonaldTask;
    busy?: boolean;
    onCancel?: (taskId: number) => void;
}

function formatCountdown(target: Date): string {
    const ms = target.getTime() - Date.now();
    if (ms <= 0) return 'any second now';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `in ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `in ${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `in ${hours}h ${minutes % 60}m`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `in ${days}d ${hours % 24}h`;
    const weeks = Math.floor(days / 7);
    return `in ${weeks}w ${days % 7}d`;
}

function formatScheduledAt(value?: string | null): string {
    if (!value) return '';
    try {
        const date = new Date(value);
        return date.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    } catch {
        return value;
    }
}

export default function RonaldFutureMessageCard({task, busy, onCancel}: RonaldFutureMessageCardProps) {
    // Tick once per second so the countdown stays live without forcing
    // the parent to re-render every tick.
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!task.scheduled_at) return undefined;
        const id = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(id);
    }, [task.scheduled_at]);

    const scheduledDate = task.scheduled_at ? new Date(task.scheduled_at) : null;
    const countdown = scheduledDate ? formatCountdown(scheduledDate) : '';
    const scheduledLabel = formatScheduledAt(task.scheduled_at);
    const channel = (task.channel || 'SMS').toUpperCase();

    return (
        <Box sx={{display: 'flex', justifyContent: 'flex-end', my: 0.5}}>
            <Sheet
                variant="outlined"
                sx={{
                    maxWidth: 520,
                    width: '100%',
                    px: 2,
                    py: 1.25,
                    borderRadius: 'md',
                    borderStyle: 'dashed',
                    borderColor: 'primary.outlinedBorder',
                    bgcolor: 'background.surface',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <AccessTimeRoundedIcon
                        fontSize="small"
                        sx={{color: 'primary.plainColor'}}
                        aria-hidden
                    />
                    <Typography level="title-sm" sx={{color: 'primary.plainColor'}}>
                        Future message
                    </Typography>
                    <Box sx={{flex: 1}}/>
                    {countdown && (
                        <Typography level="body-xs" color="neutral">
                            {countdown}
                        </Typography>
                    )}
                </Box>

                {task.follow_up_hint && (
                    <Typography
                        level="body-sm"
                        sx={{
                            fontStyle: 'italic',
                            color: 'text.secondary',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {task.follow_up_hint}
                    </Typography>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                    }}
                >
                    {onCancel ? (
                        <Button
                            size="sm"
                            variant="plain"
                            color="neutral"
                            startDecorator={<CancelRoundedIcon fontSize="small"/>}
                            disabled={busy}
                            onClick={() => onCancel(task.id)}
                        >
                            Cancel
                        </Button>
                    ) : (
                        <Box/>
                    )}
                    {scheduledLabel && (
                        <Typography level="body-xs" color="neutral">
                            {channel} · {scheduledLabel}
                        </Typography>
                    )}
                </Box>
            </Sheet>
        </Box>
    );
}
